/** @fileoverview Pure helpers for KPI Asset settings. */

/** Safe JSON.parse for localStorage reads — bad/missing values fall back. */
export function safeParseJSON(raw, fallback = null) {
  if (raw == null) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/** Source label: unit code when present, "City - State" otherwise. */
export function getSourceLabel(source) {
  if (!source) return "—";
  return source.unitCode
    ? source.unitCode
    : `${source.location?.city ?? ""} - ${source.location?.state ?? ""}`;
}

/** Unit label: "KMC - T1" when sub-location exists, "KMC" when standalone. */
export function getUnitLabel(source, subLocationObj) {
  if (!source) return "—";
  const srcLabel = getSourceLabel(source);
  if (subLocationObj?.subLocation) return `${srcLabel} - ${subLocationObj.subLocation}`;
  return srcLabel;
}

/**
 * Resolve an asset's scope tag: the source/sub-location unit label when it is
 * location-bound (and the source still exists), "No location" otherwise.
 */
export function assetScopeLabel(asset, sources) {
  const safeSources = sources || [];
  const sourceObj = safeSources.find(s => String(s.id) === String(asset.sourceId));
  const subObj = sourceObj?.subLocation?.find(s => String(s.id) === String(asset.subLocationId));
  const hasLocation = asset.sourceId != null && !!sourceObj;
  return {
    hasLocation,
    unitLabel: hasLocation ? getUnitLabel(sourceObj, subObj) : "No location",
  };
}

/**
 * Kind of entries a KPI already holds: "location" | "global" | null (none yet).
 * tinyint comes back as 0/1, not false/true — use truthy/falsy checks.
 */
export function kpiAssetKind(assets) {
  if (!assets?.length) return null;
  const hasGlobal = assets.some(a => !a.isLocationRequired);
  const hasLocation = assets.some(a => !!a.isLocationRequired);
  if (hasGlobal && !hasLocation) return "global";
  if (hasLocation && !hasGlobal) return "location";
  return null;
}

export function normalizeTitle(title) {
  return (title || "").trim().toLowerCase();
}

/**
 * The uniqueness bucket an asset lives in: the KPI-wide global bucket, or one
 * specific (source, sub-location) unit. Derives it once from an existing asset.
 */
export function bucketOf(asset) {
  return {
    isLocationRequired: !!asset.isLocationRequired,
    sourceId: asset.sourceId,
    subLocationId: asset.subLocationId,
  };
}

/**
 * Compares any asset against a bucket (see {@link bucketOf}). Single source of
 * truth for every duplicate check (add, rename, cross-KPI match) — do not fork
 * this logic.
 */
export function inBucket(asset, bucket) {
  if (!bucket.isLocationRequired) {
    return asset.sourceId == null || !asset.isLocationRequired;
  }
  return (
    String(asset.sourceId) === String(bucket.sourceId) &&
    String(asset.subLocationId ?? "") === String(bucket.subLocationId ?? "")
  );
}

/**
 * Identity key for cross-KPI asset matching: same title + same scope + same
 * unit means "the same asset" wherever it appears. There is no server-side
 * cross-KPI asset id (assetId is per KPI), so this key is the single source of
 * truth for both the cross-KPI rename/delete flows and the asset-first view.
 */
export function crossKpiKey(asset) {
  return asset.isLocationRequired
    ? `L|${normalizeTitle(asset.assetTitle)}|${asset.sourceId}|${asset.subLocationId ?? ""}`
    : `G|${normalizeTitle(asset.assetTitle)}`;
}

/**
 * Inverts kpiAssetMap: one group per distinct asset (by crossKpiKey), each
 * listing the KPIs that hold it. `asset` is a representative member — title,
 * scope and unit are identical across the group by construction.
 */
export function groupAssetsAcrossKpis(allQuestions, kpiAssetMap) {
  const groups = new Map();
  for (const q of allQuestions) {
    for (const a of kpiAssetMap[q.questionId] || []) {
      const key = crossKpiKey(a);
      if (!groups.has(key)) groups.set(key, { key, asset: a, members: [] });
      groups.get(key).members.push({ id: a.id, kpiId: q.questionId, kpiTitle: q.title, asset: a });
    }
  }
  return [...groups.values()];
}

/**
 * Builds one row of the saveMappings payload. Single source of truth for the
 * scope convention shared by the add panels: `sourceId` is null for global
 * assets, and `subLocationId` is undefined when absent so JSON serialization
 * omits it entirely.
 */
export function buildMapping({ kpiId, assetTitle, isLocationRequired, sourceId, subLocationId }) {
  const loc = !!isLocationRequired;
  return {
    kpiId: Number(kpiId),
    sourceId: loc && sourceId ? Number(sourceId) : null,
    subLocationId: loc && subLocationId ? Number(subLocationId) : undefined,
    assetTitle,
    isLocationRequired: loc,
  };
}

/**
 * Does `title` already exist among `assets` inside `bucket`?
 * @param {?number|string} excludeAssetId Skips one asset (the one being renamed).
 */
export function titleExistsIn(assets, title, bucket, excludeAssetId = null) {
  const t = normalizeTitle(title);
  if (!t) return false;
  return (assets || []).some(a => {
    if (excludeAssetId != null && String(a.id) === String(excludeAssetId)) return false;
    if (normalizeTitle(a.assetTitle) !== t) return false;
    return inBucket(a, bucket);
  });
}
